import { useSelector } from "react-redux";
import { Tags, MessageSquare, ShieldCheck, Link2 } from "lucide-react";
import { useReportFilters } from "../../contexts/ReportFiltersContext";
import { formatNumber, formatPercent } from "../../utils/format";
import EmptyState from "../../components/EmptyState";
import ReportSectionCard from "../../components/ReportSectionCard";
import TagDistributionList from "./components/TagDistributionList";
import TagsCoverageChart from "./components/TagsCoverageChart";
import { useTagsReport } from "./hooks/useTagsReport";

const KpiChip = ({ icon: Icon, label, value, hint, tone = "default" }) => {
  const toneClasses = {
    default: "bg-white border-gray-200 text-gray-700",
    blue: "bg-blue-50 border-blue-100 text-blue-700",
    emerald: "bg-emerald-50 border-emerald-100 text-emerald-700",
    slate: "bg-slate-50 border-slate-100 text-slate-700",
  };
  return (
    <div
      className={`flex-1 min-w-[160px] rounded-xl border p-3 ${toneClasses[tone]}`}
    >
      <div className="flex items-center gap-1.5 text-xs font-medium opacity-80">
        <Icon size={12} />
        <span className="uppercase tracking-wide">{label}</span>
      </div>
      <div className="text-2xl font-semibold tabular-nums mt-1 leading-tight">
        {value}
      </div>
      {hint && <div className="text-[11px] opacity-70 mt-0.5">{hint}</div>}
    </div>
  );
};

const TagsReportPage = () => {
  const { shopId, from, to, isRangeValid } = useReportFilters();
  const auth = useSelector((s) => s.auth);
  const accountId = auth?.user?.accountId;

  const { data, loading, error } = useTagsReport({
    shopId,
    accountId,
    from,
    to,
  });

  if (!shopId || !isRangeValid) {
    return (
      <div className="p-6">
        <EmptyState title="Chọn Zalo để xem báo cáo" />
      </div>
    );
  }

  const meta = data?.meta;
  const total = Number(meta?.total || 0);

  return (
    <div className="p-4 space-y-4 max-w-7xl">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">
          Nhãn &amp; Phân loại
        </h2>
        <p className="text-xs text-gray-500 mt-0.5">
          Tình trạng tag hiện tại của contacts có hoạt động gần nhất trong
          khoảng đã chọn, gồm cả contact cá nhân và group
        </p>
      </div>

      {error && (
        <div className="p-3 bg-red-50 text-red-700 rounded text-sm">{error}</div>
      )}

      <div className="flex flex-wrap gap-3">
        <KpiChip
          icon={Tags}
          label="Tổng contacts"
          value={formatNumber(total)}
          hint="Có hoạt động gần nhất trong khoảng"
          tone="slate"
        />
        <KpiChip
          icon={ShieldCheck}
          label="Có tag Akabiz"
          value={formatNumber(meta?.withAkabizTag || 0)}
          hint={
            total > 0
              ? `${formatPercent(meta?.withAkabizTag, total)} trên tổng`
              : "—"
          }
          tone="blue"
        />
        <KpiChip
          icon={MessageSquare}
          label="Có tag Zalo"
          value={formatNumber(meta?.withZaloTag || 0)}
          hint={
            total > 0
              ? `${formatPercent(meta?.withZaloTag, total)} trên tổng`
              : "—"
          }
          tone="emerald"
        />
        <KpiChip
          icon={Link2}
          label="Có cả hai"
          value={formatNumber(meta?.withBothTypes || 0)}
          hint={
            total > 0
              ? `${formatPercent(meta?.withBothTypes, total)} trên tổng`
              : "—"
          }
          tone="default"
        />
      </div>

      {meta?.truncated && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-3 py-2">
          Kết quả đã cắt bớt vì vượt giới hạn truy vấn. Hãy thu hẹp khoảng ngày
          để số liệu chính xác.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ReportSectionCard
          title="Tag Akabiz"
          subtitle="Tag nội bộ hiện đang gắn trên contacts trong tập lọc"
          right={
            <span className="text-[11px] text-gray-500">
              {data?.akabizTags?.length || 0} tag hiện có
            </span>
          }
        >
          <TagDistributionList
            tags={data?.akabizTags || []}
            total={total}
            loading={loading}
            emptyLabel="Chưa có tag Akabiz nào xuất hiện trong tập lọc"
            zeroCountLabel="Không có contact nào đang mang tag Akabiz trong tập lọc"
          />
        </ReportSectionCard>

        <ReportSectionCard
          title="Tag Zalo"
          subtitle="Tag Zalo hiện đang gắn trên contacts trong tập lọc"
          right={
            <span className="text-[11px] text-gray-500">
              {data?.zaloTags?.length || 0} tag hiện có
            </span>
          }
        >
          <TagDistributionList
            tags={data?.zaloTags || []}
            total={total}
            loading={loading}
            emptyLabel="Chưa có tag Zalo nào xuất hiện trong tập lọc"
            zeroCountLabel="Không có contact nào đang mang tag Zalo trong tập lọc"
          />
        </ReportSectionCard>
      </div>

      <ReportSectionCard
        title="Tổ hợp tag hiện tại"
        subtitle="Phân loại contacts theo trạng thái gắn tag hiện tại"
      >
        <TagsCoverageChart meta={meta} loading={loading} />
      </ReportSectionCard>

      <div className="pt-1 text-xs text-gray-400">
        Tag Akabiz lấy từ contacts.tag_ids. Tag Zalo lấy từ
        tag_zalos.conversations. Bộ lọc ngày dựa trên
        contacts.last_updated_time và bao gồm cả contact cá nhân lẫn group.
      </div>
    </div>
  );
};

export default TagsReportPage;
